// Status.jsx - System status page for CodeRated
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Eye, 
  Activity, 
  Server, 
  Database, 
  Globe, 
  Zap, 
  Brain,
  Shield,
  BarChart3,
  Clock,
  Calendar,
  RefreshCw,
  ExternalLink,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

export default function Status() {
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');

  // Simulated real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdated(new Date());
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const systemStatus = 'operational'; // operational, degraded, outage

  const services = [
    {
      name: 'Website Analysis API',
      status: 'operational',
      description: 'Core AI analysis engine',
      uptime: '99.98%',
      responseTime: '1.2s',
      icon: Brain
    },
    {
      name: 'Web Interface',
      status: 'operational', 
      description: 'CodeRated web application',
      uptime: '99.99%',
      responseTime: '450ms',
      icon: Globe
    },
    {
      name: 'Authentication',
      status: 'operational',
      description: 'User login and registration',
      uptime: '99.97%',
      responseTime: '280ms',
      icon: Shield
    },
    {
      name: 'Database',
      status: 'operational',
      description: 'Data storage and retrieval',
      uptime: '99.99%',
      responseTime: '120ms',
      icon: Database
    },
    {
      name: 'CDN & Assets',
      status: 'operational',
      description: 'Global content delivery',
      uptime: '99.95%',
      responseTime: '85ms',
      icon: Server
    },
    {
      name: 'Email System',
      status: 'operational',
      description: 'Notifications and reports',
      uptime: '99.92%',
      responseTime: '2.1s',
      icon: Activity
    }
  ];

  const metrics = [
    {
      label: 'Analyses Completed',
      value: '847,293',
      change: '+12.4%',
      trend: 'up',
      period: 'last 24h'
    },
    {
      label: 'Average Response Time',
      value: '1.2s',
      change: '-0.3s',
      trend: 'up',
      period: 'last 24h'
    },
    {
      label: 'Success Rate',
      value: '99.94%',
      change: '+0.02%',
      trend: 'up',
      period: 'last 24h'
    },
    {
      label: 'Active Users',
      value: '23,847',
      change: '+8.1%',
      trend: 'up',
      period: 'last 24h'
    }
  ];

  const incidents = [
    {
      id: 1,
      date: '2025-01-15',
      time: '14:23 PST',
      title: 'Scheduled Maintenance - Database Optimization',
      status: 'resolved',
      severity: 'maintenance',
      duration: '45 minutes',
      description: 'Performed routine database optimization to improve query performance.',
      updates: [
        { time: '15:08 PST', message: 'Maintenance completed successfully. All services restored.' },
        { time: '14:23 PST', message: 'Maintenance started. Analysis API may experience brief delays.' }
      ]
    },
    {
      id: 2,
      date: '2025-01-12',
      time: '09:15 PST',
      title: 'Degraded Performance - Analysis API',
      status: 'resolved',
      severity: 'minor',
      duration: '23 minutes',
      description: 'Increased response times for website analysis due to high traffic volume.',
      updates: [
        { time: '09:38 PST', message: 'Issue resolved. Response times back to normal.' },
        { time: '09:15 PST', message: 'Investigating increased response times.' }
      ]
    },
    {
      id: 3,
      date: '2025-01-08',
      time: '16:45 PST',
      title: 'Brief Service Interruption',
      status: 'resolved',
      severity: 'major',
      duration: '8 minutes',
      description: 'Network connectivity issues affected access to the web interface.',
      updates: [
        { time: '16:53 PST', message: 'Service fully restored. Root cause identified and addressed.' },
        { time: '16:45 PST', message: 'Investigating connectivity issues affecting web interface.' }
      ]
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'text-green-400';
      case 'degraded': return 'text-yellow-400';
      case 'outage': return 'text-red-400';
      default: return 'text-slate-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational': return CheckCircle;
      case 'degraded': return AlertTriangle;
      case 'outage': return XCircle;
      default: return AlertCircle;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'maintenance': return 'bg-blue-500/10 border-blue-500/30 text-blue-400';
      case 'minor': return 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400';
      case 'major': return 'bg-red-500/10 border-red-500/30 text-red-400';
      default: return 'bg-slate-500/10 border-slate-500/30 text-slate-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      {/* Navigation */}
      <nav className="relative z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="group flex items-center gap-4 p-2 hover:scale-110 transition-all duration-300">
              <div className="relative">
                <div className="w-12 h-10 bg-gradient-to-b from-gray-300 to-gray-500 rounded-lg border-2 border-gray-600 shadow-xl">
                  <div className="w-8 h-5 bg-black mx-auto mt-1 rounded border border-gray-800 flex items-center justify-center">
                    <Eye className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex justify-center mt-0.5 gap-1">
                    <div className="w-1 h-0.5 bg-red-500 rounded"></div>
                    <div className="w-1 h-0.5 bg-green-500 rounded"></div>
                  </div>
                </div>
              </div>
              <span className="text-2xl font-bold text-white">CodeRated</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <a 
                href="https://twitter.com/coderated" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-white transition-colors flex items-center gap-1"
              >
                Status Updates
                <ExternalLink className="w-4 h-4" />
              </a>
              <Link to="/" className="text-slate-300 hover:text-white transition-colors">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          
          {/* Hero Section */}
          <section className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className={`w-4 h-4 rounded-full ${systemStatus === 'operational' ? 'bg-green-400' : systemStatus === 'degraded' ? 'bg-yellow-400' : 'bg-red-400'} animate-pulse`} />
              <span className={`text-lg font-semibold ${getStatusColor(systemStatus)}`}>
                All Systems Operational
              </span>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-black mb-8">
              <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
                System Status
              </span>
            </h1>
            
            <p className="text-2xl md:text-3xl text-slate-300 mb-6 font-light max-w-4xl mx-auto">
              Real-time monitoring of CodeRated's 
              <span className="text-blue-400 font-semibold"> platform health</span> and performance.
            </p>

            <div className="flex items-center justify-center gap-2 text-slate-400">
              <Clock className="w-4 h-4" />
              <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
              <button 
                onClick={() => setLastUpdated(new Date())}
                className="ml-2 p-1 hover:text-white transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </section>

          {/* Current Status Overview */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Service Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                const StatusIcon = getStatusIcon(service.status);
                
                return (
                  <div key={index} className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-900/80 transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <IconComponent className="w-6 h-6 text-blue-400" />
                        <div>
                          <h3 className="text-lg font-bold text-white">{service.name}</h3>
                          <p className="text-slate-400 text-sm">{service.description}</p>
                        </div>
                      </div>
                      <StatusIcon className={`w-5 h-5 ${getStatusColor(service.status)}`} />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <div className="text-slate-400 text-xs mb-1">Uptime (30d)</div>
                        <div className="text-white font-semibold">{service.uptime}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 text-xs mb-1">Response Time</div>
                        <div className="text-white font-semibold">{service.responseTime}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Performance Metrics */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Performance Metrics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {metrics.map((metric, index) => (
                <div key={index} className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 text-center">
                  <div className="text-3xl font-black mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    {metric.value}
                  </div>
                  <div className="text-white font-medium mb-2">{metric.label}</div>
                  <div className="flex items-center justify-center gap-1 text-sm">
                    <TrendingUp className={`w-4 h-4 ${metric.trend === 'up' ? 'text-green-400' : 'text-red-400'}`} />
                    <span className={metric.trend === 'up' ? 'text-green-400' : 'text-red-400'}>
                      {metric.change}
                    </span>
                    <span className="text-slate-500 ml-1">{metric.period}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Historical Uptime */}
          <section className="mb-16">
            <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Historical Uptime</h2>
                <div className="flex gap-2">
                  {['24h', '7d', '30d', '90d'].map(timeframe => (
                    <button
                      key={timeframe}
                      onClick={() => setSelectedTimeframe(timeframe)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        selectedTimeframe === timeframe
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                      }`}
                    >
                      {timeframe}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Simplified uptime bars visualization */}
              <div className="space-y-4">
                {services.slice(0, 3).map((service, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{service.name}</span>
                      <span className="text-slate-400">{service.uptime}</span>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-green-400 h-2 rounded-full"
                        style={{ width: service.uptime }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Recent Incidents */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Recent Incidents</h2>
            <div className="space-y-6">
              {incidents.map((incident) => (
                <div key={incident.id} className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(incident.severity)}`}>
                          {incident.severity.toUpperCase()}
                        </span>
                        <div className="flex items-center gap-2 text-slate-400 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>{incident.date} at {incident.time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-400 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>{incident.duration}</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{incident.title}</h3>
                      <p className="text-slate-400 mb-4">{incident.description}</p>
                      
                      <div className="space-y-2">
                        {incident.updates.map((update, updateIndex) => (
                          <div key={updateIndex} className="flex gap-3 text-sm">
                            <span className="text-slate-500 shrink-0">{update.time}</span>
                            <span className="text-slate-300">{update.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-400 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Subscribe for Updates */}
          <section className="text-center">
            <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 backdrop-blur-xl border border-blue-500/30 rounded-3xl p-12">
              <h2 className="text-3xl font-bold text-white mb-6">Stay Informed</h2>
              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                Get notified about system updates, maintenance windows, and service incidents.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
                >
                  <Activity className="w-5 h-5" />
                  <span>Subscribe to Updates</span>
                </Link>
                <a
                  href="https://twitter.com/coderated"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-slate-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800/50 transition-all flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-5 h-5" />
                  <span>Follow on Twitter</span>
                </a>
              </div>
              
              <p className="text-slate-500 text-sm mt-6">
                Historical uptime: 99.97% • Average response time: 1.2s
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}